import React, { useContext } from 'react';
import * as R from 'ramda';
import { useStoreState, useStoreActions } from 'easy-peasy';
import Fok from './FokCounter/Fok';
import Decline from './Decline';
import { Alert, Tooltip } from 'Components/nui';
import Tabs, { TabPane } from 'Components/nui/Tabs';
import { parseSearch } from '~/utils';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import CounterTab from './Counters';
import { FormattedMessage } from 'react-intl';
import { useCounterOrders } from './Counters/helpers';
import { CounterSummaryTooltip } from './Counters/CounterSummaryTooltip';
import { QA } from 'Components/QA';
import { ApiContext } from '~/context';
import { useOrderPermissions } from '~/hooks';

export default ({ order, product, setCurCur, doRefresh }) => {
  const history = useHistory();
  const location = useLocation();
  const questionsCount = useStoreState(state =>
    state.marketdepth.orderQuestionsCount(order.id)
  );

  const countertype = order.type === 'offer' ? 'bid' : 'offer';

  const { counters } = useCounterOrders(order);

  const can = useOrderPermissions(order, product);

  const { tab } = parseSearch(R.path(['location', 'search'], history));

  const onTabClick = key =>
    history.push(
      `${location.pathname}?${qs.stringify({
        order: order.pid,
        tab: key,
      })}`
    );
  const defaultKey = order?.counteronly ? 'counters' : tab;

  const loadOrderQuestions = useStoreActions(
    action => action.marketdepth.loadOrderQuestions
  );
  const api = useContext(ApiContext);

  return (
    <Tabs
      animated={false}
      defaultActiveKey={defaultKey}
      activeKey={tab in can && can[tab] ? tab : defaultKey}
      onTabClick={onTabClick}
    >
      <TabPane
        tab={
          <FormattedMessage
            id="marketrow-tabs-header-trade"
            description="Header for Trade on tabs in Marketrow"
            defaultMessage="Trade"
          />
        }
        key="trade"
      >
        {order.tradeable ? (
          <Fok order={order} product={product} setCurCur={setCurCur} />
        ) : (
          <>
            {order.is_owner ? (
              <>
                <Alert type="warning" className="has-icon mt-10 mb-10">
                  <h4>
                    <FormattedMessage
                      id="marketrow-tabs-trade-label-nottradeable"
                      description="Label for `Not tradeable reason` field on qa in Marketrow"
                      defaultMessage="Not tradeable reason"
                    />
                  </h4>
                  {order.reason}
                </Alert>

                {order.isdeclined && (
                  <>
                    <hr className="blank mt-0 show-for-medium" />
                    <Alert className="has-icon mt-10" type="error">
                      <h4>
                        <FormattedMessage
                          id="marketrow-tabs-trade-warning-orderdeclined"
                          description="warning for `Your order was declined` on trade tab in Marketrow"
                          defaultMessage="Your order was declined"
                        />
                      </h4>
                      {order.declinereason}
                    </Alert>
                  </>
                )}
              </>
            ) : order.counteronly ? (
              // I'm going to put an ugly-ass comment here so I can complain
              // about this ugly-ass formatting.
              <Alert type="warning" hasicon>
                <FormattedMessage
                  id="marketrow-tabs-trade-sfc-warning"
                  defaultMessage="This {orderType} is subject to final confirmation (SFC). Select the Counters tab to place a firm {counterType}."
                  values={{
                    orderType: order.type,
                    counterType: countertype,
                    strong: chunks => (
                      <strong className="all-black inline">{chunks}</strong>
                    ),
                  }}
                />
              </Alert>
            ) : (
              <Alert className="has-icon mt-10" type="error">
                <h4>
                  <FormattedMessage
                    id="marketrow-tabs-trade-label-nottradeable"
                    description="Label for `Not tradeable reason` field on qa in Marketrow"
                    defaultMessage="Not tradeable reason"
                  />
                </h4>
                {/* I'm very VERY sorry */}
                {order.reason === 'Insufficient available credit' &&
                order.type === 'offer' ? (
                  <FormattedMessage
                    id="marketrow-tabs-trade-warning-nocredit"
                    description="warning for `no credit` on trade tab in Marketrow"
                    defaultMessage='Insufficient available credit. Contact "{name}" to request credit extension'
                    values={{ name: R.path(['division', 'name'], order) }}
                  />
                ) : (
                  order.reason
                )}
              </Alert>
            )}
            <hr className="blank mt-0 show-for-medium" />
          </>
        )}
      </TabPane>

      {can.decline && (
        <TabPane
          tab={
            <FormattedMessage
              id="marketrow-tabs-header-decline"
              description="Header for Decline on tabs in Marketrow"
              defaultMessage="Decline"
            />
          }
          key="decline"
        >
          {order.isdeclined ? (
            <Alert className="has-icon" type="warning">
              <h4>
                <FormattedMessage
                  id="marketrow-tabs-trade-label-decliningreason"
                  description="Label for `Reason for declining` field on qa in Marketrow"
                  defaultMessage="Reason for declining"
                />
              </h4>
              {order.declinereason}
            </Alert>
          ) : (
            <Decline order={order} />
          )}
        </TabPane>
      )}

      <TabPane
        tab={
          <>
            <FormattedMessage
              id="marketrow-tabs-header-counters"
              description="Header for Counters on tabs in Marketrow"
              defaultMessage="Counters"
            />
            {!!counters.length && (
              <CounterSummaryTooltip counters={counters}>
                <span className="count">{counters.length}</span>
              </CounterSummaryTooltip>
            )}
          </>
        }
        key="counters"
      >
        <CounterTab order={order} can={{ add: can.counter }} />
      </TabPane>

      {can.qa && (
        <TabPane
          tab={
            <>
              <FormattedMessage
                id="marketrow-tabs-header-qa"
                description="Header for Q/A on tabs in Marketrow"
                defaultMessage="Q&A"
              />
              {!!questionsCount && (
                <Tooltip
                  title={
                    <span className="icon-tooltip">
                      <span className="icon-info-circled" />
                      <FormattedMessage
                        id="questions-and-answers"
                        defaultMessage="Questions and answers"
                      />
                    </span>
                  }
                >
                  <span className="count notification">{questionsCount}</span>
                </Tooltip>
              )}
            </>
          }
          key="qa"
        >
          <QA
            order={order}
            isClosed={false}
            isOwner={order.is_owner || order.is_broker}
            canAsk={() =>
              useStoreState(
                state =>
                  !order.is_owner &&
                  state.auth.solution.roles
                    .filter(r =>
                      order.type === 'offer'
                        ? r.division.buyer
                        : r.division.seller
                    )
                    .map(r => r.role)
                    .filter(role => role !== 'guest').length > 0
              )
            }
            canAnswer={() => order.is_owner || order.is_broker}
            loadQuestions={() => loadOrderQuestions(order.id)}
            askAction={async data => {
              const result = await api.postData(
                `/orders/${order.id}/questions`,
                data
              );
              loadOrderQuestions(order.id);
              return result;
            }}
            answerAction={async (data, question) => {
              await api.postData(
                `/orders/${order.id}/questions/${question.id}`,
                data
              );
              loadOrderQuestions(order.id);
            }}
          />
        </TabPane>
      )}
    </Tabs>
  );
};
